<template>
  <div
    class="site"
    :class="{ debug: debug }"
    :style="{
      top: marginVertical + 'px ',
      bottom: marginVertical + 'px ',
      left: +marginHorizontal + 'px',
      right: +marginHorizontal + 'px'
    }"
    data-test-id="signage-site"
    @click="$router.push('/')"
  >
    <warning-message
      v-if="!site.loaded"
      is-loading-message
    >
      {{ $t('status.loading') }}
    </warning-message>
    <div v-else-if="site">
      <warning-message
        v-if="movies.length == 0"
      >
        {{ $t('showing.noShowingsToday') }}
      </warning-message>

      <!-- We have a showtimes-->
      <div class="main-display">
        <div>
          <img
            class="site-logo"
            :src="site.lightLogoImageUrl"
          >
          <div class="time text-uppercase">
            {{ site.siteTime.format("h:mm a") }}
          </div>
        </div>
        <div class="movie-grid">
          <div
            v-for="(showings, movieId) in movies"
            :key="movieId"
            class="movie-container"
          >
            <div class="movie">
              <div
                class="poster"
                :class="{ cover: cover }"
                :style="{
                  backgroundImage: `url(${showings[0].movie.posterImageUrl})`,
                  height: `${posterHeight}`
                }"
              />
              <div
                class="movie-name row justify-center items-center q-py-sm"
                :style="`${isComponent ? 'font-size: 1.3vw; margin-bottom: 50%' : 'font-size: 3vh;'}`"
              >
                {{ showings[0].movie.name }}
              </div>
              <div
                v-if="site.signageDisplayRating"
                class="row column justify-center items-center q-my-md"
              >
                <rated
                  v-if="showings[0].movie.rating"
                  :rated="showings[0].movie.rating"
                  :rating-reason="showings[0].movie.ratingReason"
                  :outline="false"
                  :color="'white'"
                  :text-color="'dark'"
                  :font-size="24"
                  :highlight-r-rated="highlightRRated"
                  :disable-official-symbol="disableOfficialSymbol"
                  class="text-bold q-px-sm"
                />
                <span
                  v-if="showings[0].movie.ratingReason && showings[0].movie.ratingReason !== ''"
                >({{ showings[0].movie.ratingReason }})</span>
              </div>
            </div>
            <div
              class="showings"
              :style="`${isComponent ? 'margin-top: 30%' : ''}`"
            >
              <div
                v-for="showing in showings"
                :key="showing.id"
                class="showing text-uppercase"
                :style="styleForShowing(showing)"
              >
                {{ showing.siteTime.format("h:mm a") }}
              </div>
              <div
                v-for="i in $_.range(maxShowings - showings.length)"
                :key="i"
                class="showing text-uppercase"
                style="color: #00000000"
              >
                none
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <warning-message
      v-else-if="errorOccurred"
    >
      A network error occurred
    </warning-message>
    <warning-message
      v-else
    >
      {{ `Unknown Site - ${$route.params.id}` }}
    </warning-message>
  </div>
</template>

<script>
import Rated from 'src/modules/consumer/components/Rated'
import WarningMessage from 'src/modules/signage/components/WarningMessage'
import ShowingsSignageConfigurationMixin from 'src/modules/signage/mixins/ShowingsSignageConfigurationMixin'
export default {
  name: 'Site',
  components: {
    Rated,
    WarningMessage
  },
  mixins: [ShowingsSignageConfigurationMixin],
  models: {
    site: {
      fields: ['id', 'name', 'primaryColor', 'lightLogoImage', 'timeZone', 'signageDisplayRating', 'migratedTo'],
      todaysShowings: {
        movie: 'default',
        screen: 'default',
        showingBadges: 'default'
      },
      variables () {
        return {
          id: this.siteId
        }
      },
      immediate: true
    }
  },
  props: {
    isComponent: {
      type: Boolean,
      required: false,
      default: false
    },
    overrideSiteId: {
      type: Number,
      required: false,
      default: null
    },
    overrideCover: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      errorOccurred: false
    }
  },
  computed: {
    titleClassIds () {
      if (!this.$route.query['title-class-ids']) return

      return _.map(this.$route.query['title-class-ids'].split(','), parseInt)
    },
    hidePast () {
      return this.$route.query['hide-past'] === 'true'
    },
    posterHeight () {
      let height = this.site.signageDisplayRating ? 35 : 40
      if (this.isComponent) {
        height = 100
      }
      const suffix = this.isComponent ? '%' : 'vh'

      return height + suffix
    },
    movies () {
      if (!this.site) return

      return _(this.site.todaysShowings.data)
        .filter(showing => !this.titleClassIds || _.includes(this.titleClassIds, showing.movie.titleClassId))
        .filter(showing => !this.hidePast || !showing.past)
        .groupBy('movie.id')
        .sortBy(showings => -showings.length) // Second priority is number of showings
        .sortBy(showings => showings[0].screen.displayOrder) // First priority is what screen it is in
        .value()
    },
    maxShowings () {
      if (!this.site || !this.site.todaysShowings) return 0

      return _(this.movies).values().map('length').max()
    },
    cover () {
      return this.overrideCover || this.$route.query.cover || false
    },
    siteId () {
      return this.overrideSiteId || this.$route.params?.siteId
    }
  },
  async mounted () {
    this.moviesIntervalId = setInterval(() => {
      this.site.fetch(true)
    }, 10 * 60 * 1000)

    await this.site.fetch()

    if (this.$signage) {
      // Fetching the site when in the app mode of digital-signage, sets the site_id cookie
      // This will reload the configuration data after being authenticated
      this.$handleAuthChange()
    }
  },
  beforeUnmount () {
    if (this.timeIntervalId) clearInterval(this.timeIntervalId)

    if (this.moviesIntervalId) clearInterval(this.moviesIntervalId)
  },
  methods: {
    styleForShowing (showing) {
      if (showing.current) {
        return 'font-size: 3.5vw; font-weight: extra-bold; ' + 'color: ' + this.$lightenColor(this.site.primaryColor, 80)
      } else if (showing.past) {
        return 'font-size: 3.5vw; font-weight: normal; text-decoration: line-through; ' + 'color: ' + this.$lightenColor(this.site.primaryColor, 20)
      } else {
        return 'font-size: 3.5vw; font-weight: bold; ' + 'color: ' + this.$lightenColor(this.site.primaryColor, 40)
      }
    }
  }
}
</script>

<style scoped>
.site {
  background-color: #161616;
  color: #fff;
  position: absolute;
}

.site.debug {
  border: solid 1px red;
}

.rating {
  height: 3vh;
  display: inline-block
}

.main-display {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  flex-direction: column;
}

.site-logo {
  max-width: 50vw;
  max-height: 10vh;
}

.time {
  position: absolute;
  top: 2vh;
  right: 2vh;
  font-size: 5vh;
}

.movie-grid {
  flex-grow: 1;

  display: flex;
}
.movie-container {
  flex-grow: 1;
  flex-basis: 0;

  display: flex;
  flex-direction: column;
  align-content: center;
}
.movie-container:nth-child(even) {
  background-color: #101010;
}
.movie-container:nth-child(odd) {
  background-color: #191919;
}
.debug .movie-container > div {
  border: solid 1px red;
}

.poster {
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
.poster.cover {
  background-size: cover;
}
.movie-name {
  text-overflow: ellipsis;

  overflow: hidden;
  text-overflow: ellipsis;

  max-height: 3em;
  height: 3em;
}

.showings {
  flex-grow: 3;

  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}
.showing {
  font-weight: 800;
  line-height: 3vw;

  display: flex;
  flex-direction: column;
  justify-content: center;

  min-width: 0;
  flex-basis: 0;
}
.debug .showing {
  border: solid 1px yellow;
}
.past {
  color: #333;
}
.current {
  color: #1e57ff;
}
</style>
